import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Award, Clock, Heart, ArrowRight, Building2, Globe2, Briefcase, Star, Target, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/animations.css';

const About = () => {
  const values = [
    {
      icon: Users,
      title: "Expert Team",
      description: "Our experienced specialists have helped hundreds of clients successfully establish their business and immigration presence in Poland."
    },
    {
      icon: Award,
      title: "Proven Success",
      description: "With a 98% success rate, we have the expertise and knowledge to guide you through the application process."
    },
    {
      icon: Clock,
      title: "Efficient Process",
      description: "We streamline the application process to save you time and reduce stress during your immigration journey."
    },
    {
      icon: Heart,
      title: "Personal Care",
      description: "Every client receives personalized attention and support throughout their entire application process."
    }
  ];

  const stats = [
    { number: "98%", label: "Success Rate", icon: Target },
    { number: "1000+", label: "Happy Clients", icon: Users },
    { number: "5+", label: "Years Experience", icon: Shield },
    { number: "24/7", label: "Support", icon: Star }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section with Minimalist Animation */}
      <section className="py-24 sm:py-60 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              About Sigma Immigration
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up" style={{ animationDelay: '200ms' }}>
              We are dedicated to making your Polish immigration journey as smooth and successful as possible. 
              With years of experience and deep knowledge of Polish immigration law, we're here to help you 
              achieve your goals of living and working in Poland.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Values Section with Floating Animation */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <Card 
                key={index} 
                className="text-center border-border hover:shadow-lg transition-all duration-500 hover:scale-105 animate-slide-in-bottom group"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <CardHeader>
                  <div className="mx-auto bg-primary/10 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors animate-float">
                    <IconComponent className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg text-foreground group-hover:text-primary transition-colors">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground group-hover:text-foreground/80 transition-colors">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Stats Section with Pulse Animation */}
        <div className="bg-primary/5 rounded-2xl p-12 mb-20 animate-fade-in backdrop-blur-sm" style={{ animationDelay: '400ms' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div 
                  key={index}
                  className="animate-slide-in-bottom group"
                  style={{ animationDelay: `${600 + index * 100}ms` }}
                >
                  <div className="mb-4 flex justify-center">
                    <div className="bg-primary/10 p-3 rounded-full animate-pulse-soft">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mission Section with Slide Animation */}
        <div className="bg-card rounded-2xl shadow-sm p-12 mb-20 border border-border overflow-hidden backdrop-blur-sm">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-4xl font-bold text-foreground mb-6 font-alumni">Our Mission</h2>
              <p className="text-muted-foreground mb-6 font-hind text-lg">
                At Sigma Immigration, we believe that everyone deserves the opportunity to build a new life in Poland. 
                Our mission is to remove the complexity and stress from the immigration process, providing clear guidance 
                and expert support every step of the way.
              </p>
              <p className="text-muted-foreground font-hind text-lg">
                We understand that immigration is more than just paperwork – it's about dreams, opportunities, and new beginnings. 
                That's why we treat every client with the respect, care, and attention they deserve.
              </p>
            </div>
            <div className="bg-accent p-8 rounded-xl animate-slide-in-right backdrop-blur-sm">
              <h3 className="text-2xl font-semibold text-foreground mb-8 font-alumni">Why Choose Us?</h3>
              <ul className="space-y-6 text-muted-foreground font-hind">
                {[
                  { icon: Building2, text: "Licensed immigration consultants" },
                  { icon: Globe2, text: "Fluent in Polish and English" },
                  { icon: Briefcase, text: "Up-to-date with latest regulations" },
                  { icon: Users, text: "Comprehensive support packages" },
                  { icon: Award, text: "Transparent pricing" }
                ].map((item, index) => {
                  const ItemIcon = item.icon;
                  return (
                    <li 
                      key={index} 
                      className="flex items-center space-x-4 animate-slide-in-right group"
                      style={{ animationDelay: `${800 + index * 100}ms` }}
                    >
                      <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
                        <ItemIcon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="group-hover:text-foreground transition-colors">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section with Scale Animation */}
        <div className="text-center bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl p-16 animate-fade-in backdrop-blur-sm" style={{ animationDelay: '1000ms' }}>
          <h2 className="text-4xl font-bold text-foreground mb-6 font-alumni animate-float">Ready to Start Your Journey?</h2>
          <p className="text-xl text-muted-foreground mb-8 font-hind max-w-2xl mx-auto">
            Let us help you navigate your business and immigration journey in Poland with confidence and ease.
          </p>
          <div className="space-x-4">
            <Button 
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-110 animate-pulse-soft"
              onClick={() => window.location.href = '/register'}
            >
              Get Started Today
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-primary text-primary hover:bg-primary/10 transition-all duration-300 hover:scale-110"
              onClick={() => window.location.href = '/contact'}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default About;
